// Presentational pieces of the CPF Clarity Explorer. Kept free of section-level
// state so the parent stays small and each piece is easy to read/test.
import React from "react";
import { SOURCE_LINKS } from "@/components/sections/cpfClarityData";

const pad2 = (n) => String(n).padStart(2, "0");

export const TopicTab = ({ topic, index, selected, visited, tabRef, onSelect, onKeyDown }) => (
  <button
    ref={tabRef}
    className="cpf-tab"
    id={`cpf-tab-${index}`}
    type="button"
    role="tab"
    aria-controls="cpf-topic-panel"
    aria-selected={selected}
    tabIndex={selected ? 0 : -1}
    data-testid={`cpf-tab-${index}`}
    data-visited={visited ? "true" : "false"}
    onClick={onSelect}
    onKeyDown={onKeyDown}
  >
    <span className="cpf-tab__number">{pad2(index + 1)}</span>
    <span className="cpf-tab__copy">
      <strong>{topic.tabTitle}</strong>
      <small>{topic.tabSub}</small>
    </span>
    <span className="cpf-tab__arrow" aria-hidden="true">&rarr;</span>
    {visited && !selected && (
      <span className="cpf-tab__seen" aria-label="Already viewed" data-testid={`cpf-tab-seen-${index}`} />
    )}
  </button>
);

export const MythCheck = ({ topic, open, onToggle }) => (
  <button
    className="cpf-myth"
    type="button"
    aria-expanded={open}
    aria-controls="cpf-myth-answer"
    data-testid="cpf-myth-button"
    onClick={onToggle}
  >
    <span className="cpf-myth__prompt">
      <small>Quick myth check</small>
      <strong>{topic.myth}</strong>
    </span>
    <span className="cpf-myth__action">{open ? "Close answer" : "Reveal answer"}</span>
    {open && (
      <span className="cpf-myth__answer" id="cpf-myth-answer" data-testid="cpf-myth-answer">
        <b className="cpf-myth__verdict">{topic.verdict}</b>
        <span>{topic.answer}</span>
      </span>
    )}
  </button>
);

export const TopicPanel = ({ topic, activeIndex, mythOpen, onToggleMyth, onCta }) => (
  <article
    className="cpf-clarity__panel is-changing"
    id="cpf-topic-panel"
    role="tabpanel"
    aria-labelledby={`cpf-tab-${activeIndex}`}
    aria-live="polite"
    key={activeIndex}
    data-testid="cpf-topic-panel"
  >
    <div className="cpf-panel__head">
      <p className="cpf-panel__kicker">{topic.kicker}</p>
      <span className="cpf-panel__badge">Official-source summary</span>
    </div>

    <h3>{topic.title}</h3>
    <p className="cpf-panel__description">{topic.description}</p>
    <ul className="cpf-panel__points">
      {topic.points.map((p) => (
        <li key={p}>{p}</li>
      ))}
    </ul>

    <MythCheck topic={topic} open={mythOpen} onToggle={onToggleMyth} />

    <div className="cpf-panel__links">
      <a className="cpf-panel__source" href={topic.sourceUrl} target="_blank" rel="noopener noreferrer">
        Verify on CPF Board: {topic.sourceLabel} &#8599;
      </a>
      <button className="cpf-panel__cta" type="button" onClick={onCta} data-testid="cpf-clarity-cta">
        Get clarity on this at the seminar <span aria-hidden="true">&rarr;</span>
      </button>
    </div>
  </article>
);

export const SourcesAside = () => (
  <aside className="cpf-clarity__sources" aria-label="Information sources">
    <div>
      <strong>Information sources</strong>
      <span>Summarised from official CPF Board pages. Refer to the source pages for the latest details and conditions.</span>
    </div>
    <nav className="cpf-clarity__source-links" aria-label="Official CPF Board sources">
      {SOURCE_LINKS.map((s) => (
        <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer">
          {s.label}
        </a>
      ))}
    </nav>
  </aside>
);
